import { XMarkIcon } from '@heroicons/react/24/outline';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import {
  ElementType,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import { cn } from '@/lib/utils/tailwind';

import { HeroIcon } from '../Icon';

import type { AsElement, PolymorphicProps } from '@/types';

type DialogCloseProps<T extends ElementType = 'button'> = PolymorphicProps<
  T,
  {
    children?: ReactNode;
    className?: string;
  }
>;

/*
required to create an inner & outer component due to radix needing forwardRef
and the fact we use generics
 */
const DialogCloseBase = forwardRef<
  HTMLElement,
  {
    as?: ElementType;
    children?: ReactNode;
    className?: string;
  } & Omit<HTMLAttributes<HTMLElement>, 'ref' | 'className' | 'children'>
>(({ as, className, children, ...props }, ref) => {
  const Component: ElementType = as || 'button';
  const t = useTranslations();
  return (
    <RadixDialog.Close asChild>
      <Component
        className={cn(
          'inline-flex items-center justify-center rounded-full p-1 hover:bg-muted transition',
          className,
        )}
        ref={ref}
        aria-label={t('CLOSE_DIALOG')}
        {...props}
      >
        {children || <HeroIcon icon={XMarkIcon} />}
      </Component>
    </RadixDialog.Close>
  );
});
DialogCloseBase.displayName = 'DialogCloseBase';

const DialogClose = (<T extends ElementType = 'button'>(
  props: DialogCloseProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
): ReactElement => {
  return <DialogCloseBase {...props} />;
}) as <T extends ElementType = 'button'>(
  props: DialogCloseProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
) => ReactElement;

Object.defineProperty(DialogClose, 'displayName', {
  value: 'DialogClose',
  configurable: true,
});

export { DialogClose, type DialogCloseProps };
